import { Injectable } from '@angular/core';

export interface QuestionTableDataContainer {
  questionWithTablePlaceholders: string;
  questionTableData: QuestionTableData[];
}

export interface QuestionTableData {
  columns: { isSticky: boolean; key: string; value: string }[];
  rows: { isSticky: boolean; cells: { [index: string]: string } }[];
}

@Injectable({
  providedIn: 'root',
})
export class QuestionTableService {
  /**
   * RegExp to convert json to html.
   */
  private readonly jsonToHtmlRegExp: RegExp = new RegExp(
    /(%DATA_TABLE_START%)([\s\S]*?)(%DATA_TABLE_END%)/g,
  );

  /**
   * To QuestionTableDataContainer.
   */
  public tablesJsonToQuestionTableData(
    value: string,
  ): QuestionTableDataContainer {
    const questionTableData: QuestionTableData[] = [];
    const questionWithTablePlaceholders: string = (value || '').replace(
      this.jsonToHtmlRegExp,
      (match) => {
        // remove placeholders from matched string + parse to QuestionTableData json
        const tableData: QuestionTableData = JSON.parse(
          match
            .replace('%DATA_TABLE_START%', '')
            .replace('%DATA_TABLE_END%', ''),
        );

        questionTableData.push(tableData);

        return '<span class="question-table-placeholder"></span>';
      },
    );

    return {
      questionWithTablePlaceholders: questionWithTablePlaceholders,
      questionTableData: this.denormalizeCellValues(questionTableData),
    };
  }

  /**
   * Parse the value for each row and column and replace %FIELD% with the gap
   * placeholder.
   */
  private denormalizeCellValues(
    tableData: QuestionTableData[],
  ): QuestionTableData[] {
    const gapElement: string = '<span class="gap"></span>';
    return tableData.map((data) => {
      const dataColumns: QuestionTableData['columns'] = data.columns ?? [];
      const dataRows: QuestionTableData['rows'] = data.rows ?? [];
      const keys: Set<string> = new Set();
      const clone: QuestionTableData = {
        columns: dataColumns.map((column) => {
          keys.add(column.key);
          return {
            ...column,
            value: column.value.replace(/%FIELD%/g, gapElement),
          };
        }),
        rows: dataRows.map((row) => {
          const cells: QuestionTableData['rows'][0]['cells'] = row.cells;
          for (const key of keys) {
            if (cells[key]) {
              cells[key] = cells[key].replace(/%FIELD%/g, gapElement);
            }
          }
          return {
            ...row,
            cells: cells,
          };
        }),
      };

      return clone;
    });
  }
}
